import React, { useEffect, useRef, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { checkoutService, stripeService, applicationService } from '../services';
import { isStripeDevMode, mockConfirmCardSetup } from '../utils/stripeDevMode';
import Button from './Button';
import DevCardElement from './DevCardElement';
import CardPaymentSection from './CardPaymentSection';

function formatCents(cents) {
  const abs = Math.abs(cents);
  return `$${(abs / 100).toFixed(2)}`;
}

/**
 * CheckoutForm — balance-first ticket checkout.
 *
 * Shows the line-item breakdown (ticket / PR fee / GST / Stripe fee) and renders a
 * CardElement only when a card portion is required. If the guest's Wallet balance
 * fully covers the total, submit is one click with no Stripe call.
 *
 * Props:
 *   eventId       — required
 *   applicationId — required (create the Application first with useCheckoutFlow=true)
 *   quantity      — default 1
 *   userEmail     — used to attach the payment method via SetupIntent
 *   onSuccess(result) — called with { applicationId, status, balancePortionCents, cardPortionCents, stripePaymentIntentId }
 *   onError(message)
 */
export default function CheckoutForm({
  eventId,
  applicationId,
  quantity: initialQuantity = 1,
  userEmail,
  onSuccess,
  onError,
  // Pay-what-you-want: per-ticket amount the guest entered on the apply form.
  // Ignored by fixed events (server ignores it too).
  customPerTicketCents = null,
  // Tiered pricing: the selected tier drives the quoted price. tierName is display-only.
  tierId = null,
  tierName = null,
}) {
  const stripe = useStripe();
  const cardRef = useRef(null);
  const [cardReady, setCardReady] = useState(false);
  // Quantity is editable here — the parent passes the initial pending_payment
  // quantity, and the stepper PATCHes the application row + refreshes the quote.
  const [quantity, setQuantity] = useState(initialQuantity);
  const [updatingQuantity, setUpdatingQuantity] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [devBehavior, setDevBehavior] = useState('success');

  async function handleChangeQuantity(delta) {
    const next = Math.max(1, Math.min(10, quantity + delta));
    if (next === quantity) return;
    setUpdatingQuantity(true);
    setError(null);
    const r = await applicationService.updateQuantity(applicationId, next);
    setUpdatingQuantity(false);
    if (r.success) {
      // Server validated capacity. Bump local quantity → triggers the quote
      // refresh effect below.
      setQuantity(next);
    } else {
      setError(r.error || 'Could not change quantity');
    }
  }

  // Promo code: input + applied state. Re-quotes whenever the applied code changes.
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, valid, reason, ... }
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingQuote(true);
      const r = await checkoutService.getQuote(eventId, quantity, appliedPromo?.code ?? null, customPerTicketCents, tierId);
      if (cancelled) return;
      if (!r.success) {
        setError(r.error || 'Unable to load quote');
      } else {
        setQuote(r.data);
      }
      setLoadingQuote(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [eventId, quantity, appliedPromo, customPerTicketCents, tierId]);

  async function handleApplyPromo() {
    const code = promoInput.trim();
    if (!code) return;
    setPromoChecking(true);
    try {
      const { default: promoCodeService } = await import('../services/promoCodeService');
      const r = await promoCodeService.validate({ eventId, code, quantity });
      if (r.success && r.data?.valid) {
        setAppliedPromo({ code: code.toUpperCase(), ...r.data });
      } else {
        setAppliedPromo({ code: code.toUpperCase(), valid: false, reason: r.data?.reason || r.error || 'Invalid code' });
      }
    } finally {
      setPromoChecking(false);
    }
  }

  function handleRemovePromo() {
    setAppliedPromo(null);
    setPromoInput('');
  }

  /**
   * Common path for any payment method (card form or wallet). When called
   * from the wallet flow, prebuiltPaymentMethodId is the PM Stripe handed us
   * via PaymentRequestButton; we still wrap it in a SetupIntent so the same
   * backend confirmCheckout codepath handles both.
   *
   * skipBalance: when true, force a card-only charge even if the wallet
   * could cover. Set when the user explicitly chose card / Apple Pay /
   * Google Pay so we don't silently consume their wallet behind their tap.
   */
  async function processPayment(prebuiltPaymentMethodId = null, skipBalance = false) {
    if (!quote) return { ok: false, message: 'Quote not loaded' };

    let stripeCustomerId = null;
    let stripePaymentMethodId = null;
    // If we're skipping balance, the card portion is the full grand total
    // regardless of what the wallet-aware quote says.
    const needsCardPayment = skipBalance || quote.cardPortionCents > 0;

    if (needsCardPayment) {
      if (isStripeDevMode()) {
        const mock = await mockConfirmCardSetup(devBehavior);
        stripePaymentMethodId = mock.setupIntent.payment_method;
        stripeCustomerId = `cus_dev_${Date.now()}`;
      } else {
        if (!stripe) return { ok: false, message: 'Stripe is not loaded' };
        const setupRes = await stripeService.createSetupIntent();
        if (!setupRes.success) return { ok: false, message: setupRes.error || 'Could not prepare payment' };
        if (prebuiltPaymentMethodId) {
          // Wallet flow — attach the wallet's PaymentMethod to the customer via
          // the SetupIntent. confirmCardSetup accepts a PaymentMethod ID.
          const result = await stripe.confirmCardSetup(setupRes.data.clientSecret, {
            payment_method: prebuiltPaymentMethodId,
          });
          if (result.error) return { ok: false, message: result.error.message };
          stripePaymentMethodId = result.setupIntent.payment_method;
        } else {
          // Card form flow — let the section element render Stripe Elements
          // and confirm using its imperative API.
          if (!cardRef.current) return { ok: false, message: 'Card form is not ready' };
          const result = await cardRef.current.confirmSetup(setupRes.data.clientSecret, { email: userEmail });
          if (result.error) return { ok: false, message: result.error };
          stripePaymentMethodId = result.paymentMethod;
        }
        stripeCustomerId = setupRes.data.customerId;
      }
    }

    const confirmRes = await checkoutService.confirmCheckout(
      eventId,
      applicationId,
      stripeCustomerId,
      stripePaymentMethodId,
      appliedPromo?.valid ? appliedPromo.code : null,
      skipBalance,
    );
    if (!confirmRes.success) return { ok: false, message: confirmRes.error || 'Checkout failed' };
    return { ok: true, data: confirmRes.data };
  }

  // Pay button on the card form — uses card for whatever the quote says is
  // the card portion. If wallet covers everything (cardPortion = 0) but the
  // guest is filling in the card form anyway, treat that as an explicit
  // "pay by card" and skip balance.
  async function handleCardSubmit(e) {
    e.preventDefault();
    if (!quote) return;
    setSubmitting(true);
    setError(null);
    const r = await processPayment(null, quote.cardPortionCents === 0);
    setSubmitting(false);
    if (r.ok) onSuccess?.(r.data);
    else { setError(r.message); onError?.(r.message); }
  }

  // Pay-from-wallet button — only shown when wallet covers the whole charge.
  async function handlePayFromWallet() {
    if (!quote) return;
    setSubmitting(true);
    setError(null);
    const r = await processPayment(null, false);
    setSubmitting(false);
    if (r.ok) onSuccess?.(r.data);
    else { setError(r.message); onError?.(r.message); }
  }

  async function handleWalletPayment(walletPaymentMethod, completeFn) {
    // Apple Pay / Google Pay always means "don't touch my wallet, charge
    // the card I just authed." Force skipBalance regardless of balance.
    setError(null);
    const r = await processPayment(walletPaymentMethod.id, true);
    completeFn(r.ok ? 'success' : 'fail');
    if (r.ok) onSuccess?.(r.data);
    else setError(r.message);
  }

  if (loadingQuote) {
    return <div className="font-mono text-xs uppercase tracking-wider">Loading quote…</div>;
  }

  if (!quote) {
    return (
      <div className="font-mono text-xs text-red-600">{error || 'Unable to load quote'}</div>
    );
  }

  const needsCard = quote.cardPortionCents > 0;
  // When wallet covers the full charge we still show the card section as an
  // alternative — Google Pay / Apple Pay / card always need to be tappable.
  const walletCoversAll = quote.balancePortionCents > 0 && !needsCard;
  // Apple Pay / Google Pay / "OR pay by card" always bypass the wallet (skipBalance=true),
  // so the Apple Pay sheet + the alternative-card button must display the FULL amount
  // the card will be charged — which is cardOnlyTotalCents (subtotal + Stripe fee on full).
  // Falls back to grandTotalCents/cardPortionCents for older backend responses pre-rollout.
  const cardSectionAmount = walletCoversAll
    ? (quote.cardOnlyTotalCents || quote.grandTotalCents)
    : (quote.cardOnlyTotalCents || quote.cardPortionCents);

  return (
    <form onSubmit={handleCardSubmit} className="space-y-4">
      {/* Quantity stepper — guests can change mind before paying. */}
      <div className="border-2 border-black p-3 flex items-center justify-between font-mono text-xs uppercase tracking-wider">
        <span className="font-bold">Quantity</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleChangeQuantity(-1)}
            disabled={updatingQuantity || quantity <= 1}
            className="w-8 h-8 border-2 border-black bg-white text-black font-bold hover:bg-black hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-black"
            aria-label="Decrease quantity"
          >
            −
          </button>
          <span className="w-6 text-center text-sm font-bold">
            {updatingQuantity ? '…' : quantity}
          </span>
          <button
            type="button"
            onClick={() => handleChangeQuantity(1)}
            disabled={updatingQuantity || quantity >= 10}
            className="w-8 h-8 border-2 border-black bg-white text-black font-bold hover:bg-black hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-black"
            aria-label="Increase quantity"
          >
            +
          </button>
        </div>
      </div>

      <div className="border border-black p-4 font-mono text-sm space-y-2">
        <div className="flex justify-between">
          <span>{quote.ticketTierName || tierName || 'Ticket'}{quantity > 1 ? ` × ${quantity}` : ''}</span>
          <span>{formatCents(quote.ticketCents)}</span>
        </div>
        {quote.promoDiscountTotalCents > 0 && (
          <div className="flex justify-between text-green-700">
            <span>Promo {quote.promoCode}</span>
            <span>-{formatCents(quote.promoDiscountTotalCents)}</span>
          </div>
        )}
        {quote.prServiceFeeCents > 0 && (
          <div className="flex justify-between">
            <span>Portal fee</span>
            <span>{formatCents(quote.prServiceFeeCents)}</span>
          </div>
        )}
        {quote.gstCents > 0 && (
          <div className="flex justify-between">
            <span>GST (5%)</span>
            <span>{formatCents(quote.gstCents)}</span>
          </div>
        )}
        {quote.stripeFeeCents > 0 && (
          <div className="flex justify-between text-gray-600">
            <span>Processing fee</span>
            <span>{formatCents(quote.stripeFeeCents)}</span>
          </div>
        )}
        <hr className="border-black" />
        <div className="flex justify-between font-bold uppercase tracking-wider">
          <span>Total</span>
          <span>{formatCents(quote.grandTotalCents)}</span>
        </div>
      </div>

      {/* Wallet usage callout — loud enough that no one wonders "wait, did
          anything just happen?" when balance covers the whole charge and
          we go straight to a Confirm button. */}
      {quote.balancePortionCents > 0 && (
        <div className="border-2 border-black bg-yellow-100 p-3 font-mono text-xs">
          <div className="flex items-center justify-between">
            <span className="uppercase tracking-wider font-bold">
              {needsCard ? 'Using your Wallet' : 'Paying from your Wallet'}
            </span>
            <span className="font-bold text-base">-{formatCents(quote.balancePortionCents)}</span>
          </div>
          {typeof quote.walletBalanceCents === 'number' && (
            <div className="mt-1 text-gray-700">
              Wallet balance: {formatCents(quote.walletBalanceCents)} → after this charge: {formatCents(Math.max(0, quote.walletBalanceCents - quote.balancePortionCents))}
            </div>
          )}
          {needsCard && (
            <div className="mt-1 text-gray-700">
              Card will cover the remaining <strong>{formatCents(quote.cardPortionCents)}</strong>.
            </div>
          )}
        </div>
      )}

      {needsCard && (
        <div className="flex justify-between font-mono text-sm font-bold uppercase tracking-wider border-2 border-black p-3">
          <span>On card</span>
          <span>{formatCents(quote.cardPortionCents)}</span>
        </div>
      )}

      {/* Promo code — hidden for tiered events, where the ticket options replace codes. */}
      {!tierId && (
      <div className="border border-black p-3 font-mono text-xs">
        {appliedPromo?.valid ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-green-700">
              Code <strong>{appliedPromo.code}</strong> applied —{' '}
              {appliedPromo.discountType === 'percentage'
                ? `${appliedPromo.discountValue}% off`
                : `${formatCents(appliedPromo.discountValue)} off`}
            </span>
            <button type="button" onClick={handleRemovePromo} className="underline">
              Remove
            </button>
          </div>
        ) : (
          <div className="flex items-stretch gap-2 min-w-0">
            <input
              type="text"
              value={promoInput}
              onChange={(e) => setPromoInput(e.target.value)}
              placeholder="Promo code?"
              className="flex-1 min-w-0 border border-black px-2 py-1 font-mono uppercase"
              maxLength={50}
              autoCapitalize="characters"
            />
            <button
              type="button"
              onClick={handleApplyPromo}
              disabled={promoChecking || !promoInput.trim()}
              className="flex-shrink-0 border border-black px-3 py-1 uppercase tracking-wider disabled:opacity-50"
            >
              {promoChecking ? '…' : 'Apply'}
            </button>
          </div>
        )}
        {appliedPromo && !appliedPromo.valid && (
          <div className="mt-2 text-red-600">{appliedPromo.reason || 'Invalid or expired code'}</div>
        )}
      </div>
      )}

      {/* Card / wallet-button section. Always rendered (so Android users see
          Google Pay and iOS users see Apple Pay) — when balance covers the
          whole charge we still surface it as an opt-in alternative. */}
      <div className="border border-black p-3">
        {isStripeDevMode() ? (
          <DevCardElement value={devBehavior} onChange={setDevBehavior} />
        ) : (
          <CardPaymentSection
            ref={cardRef}
            amountCents={cardSectionAmount}
            currency="cad"
            countryDefault="CA"
            onValidityChange={setCardReady}
            onWalletPaymentMethod={handleWalletPayment}
            disabled={submitting}
            label={walletCoversAll ? 'OR PAY WITH CARD' : 'PAY WITH CARD'}
          />
        )}
      </div>

      {error && (
        <div className="border border-red-600 bg-red-50 text-red-700 font-mono text-xs p-2">
          {error}
        </div>
      )}

      {walletCoversAll ? (
        // Wallet-covered case: pay-from-wallet is the primary action,
        // pay-by-card is the secondary submit (form's default action).
        <div className="space-y-2">
          <Button type="button" onClick={handlePayFromWallet} disabled={submitting}>
            {submitting ? 'Processing…' : `Pay ${formatCents(quote.grandTotalCents)} from Wallet`}
          </Button>
          <Button
            type="submit"
            disabled={submitting || (!isStripeDevMode() && !cardReady)}
            className="!bg-white !text-black hover:!bg-black hover:!text-white"
          >
            {submitting ? 'Processing…' : `Or pay ${formatCents(quote.cardOnlyTotalCents || quote.grandTotalCents)} by card`}
          </Button>
        </div>
      ) : (
        <Button type="submit" disabled={submitting || (needsCard && !isStripeDevMode() && !cardReady)}>
          {submitting ? 'Processing…' : `Pay ${formatCents(quote.cardPortionCents || quote.grandTotalCents)}`}
        </Button>
      )}
    </form>
  );
}
